import useChartSelections from "components/charting/hooks/useChartSelections";
import type { ProfilingChartEntityId, SelectedChartMeasureExercise } from "components/charting/types";
import type { LineData } from "components/charting/types/Chart.types";
import UnitHelper from "helpers/UnitHelper";
import usePreferences from "hooks/usePreferences";
import { useMemo } from "react";
import { MetadataType } from "types";
import type { LoadVelocityProfile } from "types/load-velocity-profile";
import useProfilingRepMetric from "../useProfilingRepMetric";

const KILOGRAM_INTERVAL = 5;
const POUND_INTERVAL = 10;

function getLoads(loadZero: number, interval: number) {
  const loads: number[] = [];
  for (let load = 0; load < loadZero; load += interval) {
    loads.push(load);
  }
  return loads.concat(loadZero);
}

/**
 * Hook to generate line data for profiling LVP (Load-Velocity Profile) chart.
 * It calculates the line points based on the athlete's LVP data and the selected chart entity.
 *
 */
function useProfilingLvpLine(lvps: LoadVelocityProfile[], profilingChartEntityId: ProfilingChartEntityId) {
  const selections = useChartSelections();
  const measure = selections.measures[0] as SelectedChartMeasureExercise;
  const source = selections.sources[0];
  const [prefs] = usePreferences();
  const repMetric = useProfilingRepMetric();

  return useMemo(() => {
    const interval = prefs.imperialSystemEnabled
      ? UnitHelper.convert(POUND_INTERVAL, UnitHelper.units.Pound, UnitHelper.units.Kilogram)
      : KILOGRAM_INTERVAL;

    return lvps.map((lvp): LineData => {
      const common = {
        entityId: profilingChartEntityId,
        source,
        measure: { id: measure.id, metricField: measure.metric?.field, repMetricField: repMetric?.field },
        metadataType: MetadataType.EXERCISE,
        sortOrder: repMetric?.sortType === 0 ? ("asc" as const) : ("desc" as const),
        variant: lvp.variant || undefined,
      };

      return {
        ...common,
        line: getLoads(lvp.loadZero, interval).map((x) => {
          const y = lvp.intercept + lvp.slope * x;
          return {
            ...common,
            x,
            y,
            metadataType: MetadataType.EXERCISE,
            lvp,
            profiling: { x, y, side: profilingChartEntityId },
          };
        }),
      };
    });
  }, [lvps, measure.id, measure.metric?.field, prefs.imperialSystemEnabled, profilingChartEntityId, repMetric, source]);
}

export default useProfilingLvpLine;
